// Static data for the Harmony page mockups.

import { MAJOR_NUMERALS, degreeOf } from "@/lib/music/degree";

export interface PopularProgression {
  id: string;
  label: string;
  numerals: string;
  /** Diatonic degree indices (0-based: 0=I, 1=ii, …). */
  degrees: number[];
  context: string;
}

/**
 * Recommended voicing for one chord of a progression.
 *
 * Stored as absolute MIDI numbers in the **C major reference**. To render
 * for another tonality, transpose by `tonicPc` (with octave wrap when the
 * pitch-class index exceeds 6, to keep the voices in a comfortable
 * register, see `transposeVoicing` in `progression-transition.tsx`).
 *
 * The voices are sorted ascending so that pairing consecutive chords by
 * index gives natural voice leading (the highest voice of one chord
 * connects to the highest voice of the next, etc.).
 */
export interface ProgressionVoicing {
  /** Bass MIDI number (LH), typically around C2-C3. */
  bass: number;
  /** Upper voicing (RH), 3 MIDI numbers sorted ascending. */
  voices: number[];
}

/**
 * Hand-picked voicings per progression in C major. The choice of
 * inversion for each chord aims to maximise common tones with the
 * previous chord, illustrating the principle of voice leading.
 *
 * Only major-mode progressions are covered, minor mode falls back to
 * naive root-position triads in `progression-drill.tsx`.
 */
export const PROGRESSION_VOICINGS: Record<string, ProgressionVoicing[]> = {
  // I-V-vi-IV : G stays from I→V, then C/A stay from vi→IV.
  "pop-axis": [
    { bass: 36, voices: [64, 67, 72] }, // I:  C2 · E4 G4 C5
    { bass: 43, voices: [62, 67, 71] }, // V:  G2 · D4 G4 B4   (G stays)
    { bass: 45, voices: [64, 69, 72] }, // vi: A2 · E4 A4 C5   (none common)
    { bass: 41, voices: [65, 69, 72] }, // IV: F2 · F4 A4 C5   (A, C stay)
  ],
  // ii-V-I : D stays ii→V, G stays V→I, the classic jazz cadence.
  "jazz-251": [
    { bass: 38, voices: [62, 65, 69] }, // ii: D2 · D4 F4 A4
    { bass: 43, voices: [62, 67, 71] }, // V:  G2 · D4 G4 B4   (D stays)
    { bass: 36, voices: [64, 67, 72] }, // I:  C2 · E4 G4 C5   (G stays)
  ],
  // I-IV-V : C stays I→IV, but IV→V has zero common tones (all step).
  "blues-145": [
    { bass: 36, voices: [60, 64, 67] }, // I:  C2 · C4 E4 G4
    { bass: 41, voices: [60, 65, 69] }, // IV: F2 · C4 F4 A4   (C stays)
    { bass: 43, voices: [62, 67, 71] }, // V:  G2 · D4 G4 B4   (none common)
  ],
  // I-V-vi-iii-IV-I-IV-V : Pachelbel's loop, descending bass.
  "canon": [
    { bass: 36, voices: [64, 67, 72] }, // I:   E4 G4 C5
    { bass: 43, voices: [62, 67, 71] }, // V:   D4 G4 B4    (G stays)
    { bass: 45, voices: [64, 69, 72] }, // vi:  E4 A4 C5    (none)
    { bass: 40, voices: [64, 67, 71] }, // iii: E4 G4 B4    (E stays)
    { bass: 41, voices: [65, 69, 72] }, // IV:  F4 A4 C5    (none)
    { bass: 36, voices: [64, 67, 72] }, // I:   E4 G4 C5    (C stays)
    { bass: 41, voices: [65, 69, 72] }, // IV:  F4 A4 C5    (C stays)
    { bass: 43, voices: [62, 67, 71] }, // V:   D4 G4 B4    (none)
  ],
  // I-vi-IV-V : two transitions with 2 common tones, then a dramatic V.
  "doo-wop": [
    { bass: 36, voices: [64, 67, 72] }, // I:  E4 G4 C5
    { bass: 45, voices: [64, 69, 72] }, // vi: E4 A4 C5    (E, C stay)
    { bass: 41, voices: [65, 69, 72] }, // IV: F4 A4 C5    (A, C stay)
    { bass: 43, voices: [62, 67, 71] }, // V:  D4 G4 B4    (none)
  ],
};

export const POPULAR_PROGRESSIONS: PopularProgression[] = [
  {
    id: "pop-axis",
    label: "Pop · « Axis »",
    numerals: "I-V-vi-IV",
    degrees: [0, 4, 5, 3],
    context: "Let It Be, Don't Stop Believin', des milliers de tubes",
  },
  {
    id: "jazz-251",
    label: "Jazz · ii-V-I",
    numerals: "ii-V-I",
    degrees: [1, 4, 0],
    context: "Cadence parfaite du jazz, résolution la plus forte",
  },
  {
    id: "blues-145",
    label: "Blues · I-IV-V",
    numerals: "I-IV-V",
    degrees: [0, 3, 4],
    context: "Le blues 12 mesures, fondement du rock",
  },
  {
    id: "canon",
    label: "Canon de Pachelbel",
    numerals: "I-V-vi-iii-IV-I-IV-V",
    degrees: [0, 4, 5, 2, 3, 0, 3, 4],
    context: "Variation classique, base de Let It Go",
  },
  {
    id: "doo-wop",
    label: "Doo-wop · 50s",
    numerals: "I-vi-IV-V",
    degrees: [0, 5, 3, 4],
    context: "Stand By Me, Earth Angel, feel rétro",
  },
];

/**
 * Functional role per diatonic degree (1..7) in major key.
 * Tonic / Subdominant / Dominant, used to colour-code chords.
 *
 * Dérivé et non recopié : `lib/music/degree` porte la table des degrés du
 * produit, et deux tables du même fait finissent par diverger. Cet écran écrit
 * `SD` là où le corpus écrit `PD` — c'est la même fonction, sous le nom que
 * ses couleurs et ses légendes portent déjà.
 *
 * Le repli sur `T` couvre les degrés dont la fonction est disputée. Aucun des
 * sept du majeur n'est dans ce cas, donc il ne se déclenche pas ici ; il évite
 * seulement qu'un trou dans la table devienne un trou dans la légende.
 */
export const DIATONIC_FUNCTION: readonly ("T" | "SD" | "D")[] =
  MAJOR_NUMERALS.map((numeral) => {
    const usual = degreeOf(numeral, "major")?.usualFunction ?? "T";

    return usual === "PD" ? "SD" : usual;
  });

export const FUNCTION_COLOR: Record<string, string> = {
  T: "rgba(196,181,253,0.85)",
  SD: "rgba(251,191,36,0.85)",
  D: "rgba(248,113,113,0.85)",
};

export const FUNCTION_LABEL: Record<string, string> = {
  T: "Tonique",
  SD: "Sous-dominante",
  D: "Dominante",
};

/** One-liner role description for the harmonic-function legend. */
export const FUNCTION_NARRATIVE: Record<string, string> = {
  T: "Le foyer, repos, résolution",
  SD: "Le départ, on s'éloigne du foyer",
  D: "La tension, appelle la résolution vers la tonique",
};

/** Diatonic degrees (1-based) belonging to each function in major. */
export const DEGREES_BY_FUNCTION: Record<string, number[]> = {
  T: [1, 3, 6],
  SD: [2, 4],
  D: [5, 7],
};
